import React, { useMemo, useState } from 'react';
import { Button, Space, type ButtonProps } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useCustomerOptions } from '../../modules/reference-data/queries/useCustomerOptions';
import type { CustomerOptionDto } from '../../dto/reference';
import CustomerPickerModal from './CustomerPickerModal';
import './CustomerPicker.css';

export interface CustomerPickerChangeContext {
  reason: 'terminal-selection' | 'clear';
  previousCustomer?: CustomerOptionDto;
}

interface CustomerPickerProps {
  value?: string | null;
  onChange?: (
    customerId: string | undefined,
    customer?: CustomerOptionDto,
    context?: CustomerPickerChangeContext,
  ) => void;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  size?: ButtonProps['size'];
  /**
   * Display name for a currently selected customer that is not present in the
   * loaded options — e.g. an archived/legacy customer on an existing order.
   * Keeps the trigger showing a readable name instead of falling back to the
   * placeholder while the id still resolves to nothing in the catalog.
   */
  fallbackLabel?: string;
  'data-testid'?: string;
}

const CustomerPicker: React.FC<CustomerPickerProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  style,
  size,
  fallbackLabel,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation(['customers', 'common']);
  const { options: customers, loading, refresh } = useCustomerOptions(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  const selectedCustomer = useMemo(
    () => customers.find((customer) => customer.id === value),
    [customers, value],
  );

  const selectedLabel = selectedCustomer?.name
    || fallbackLabel
    || placeholder
    || t('customers:customerPicker.placeholder');

  return (
    <>
      <Space.Compact className="customer-picker-field" style={style}>
        <Button
          block
          size={size}
          disabled={disabled}
          loading={loading && !selectedCustomer}
          icon={<SearchOutlined aria-hidden="true" />}
          onClick={() => setPickerOpen(true)}
          data-testid={dataTestId}
          className="customer-picker-field__select"
        >
          <span className="customer-picker-field__label">{selectedLabel}</span>
        </Button>
        {value && !disabled && (
          <Button
            size={size}
            icon={<CloseOutlined aria-hidden="true" />}
            aria-label={t('common:clear')}
            onClick={() => onChange?.(undefined, undefined, {
              reason: 'clear',
              previousCustomer: selectedCustomer,
            })}
          />
        )}
      </Space.Compact>

      <CustomerPickerModal
        open={pickerOpen}
        customers={customers}
        loading={loading}
        selectedCustomerId={value}
        onRefresh={refresh}
        onSelect={(customer) => {
          onChange?.(customer.id, customer, {
            reason: 'terminal-selection',
            previousCustomer: selectedCustomer,
          });
          setPickerOpen(false);
        }}
        onCancel={() => setPickerOpen(false)}
      />
    </>
  );
};

export default CustomerPicker;
